import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SALUTATIONS = ["Frau", "Herr", "Divers", "Firma"];

export function Section({ title, children, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
      <h2 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">{title}</h2>
      <div className="grid grid-cols-6 gap-3">{children}</div>
    </section>
  );
}

const SPAN = { 1: "sm:col-span-1", 2: "sm:col-span-2", 3: "sm:col-span-3", 4: "sm:col-span-4", 6: "sm:col-span-6" };

export function F({ label, span = 3, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div className={`col-span-6 ${SPAN[span]}`}>
      <Label>{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={testid} />
    </div>
  );
}

function SalutationSelect({ value, onChange, testid }) {
  return (
    <div className="col-span-6 sm:col-span-1">
      <Label>Anrede</Label>
      <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">–</SelectItem>
          {SALUTATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function PersonFields({ data, set, prefix }) {
  return (
    <>
      <SalutationSelect value={data.salutation} onChange={(v) => set("salutation", v)} testid={`${prefix}-salutation`} />
      <F label="Titel" span={1} value={data.title} onChange={(v) => set("title", v)} testid={`${prefix}-title`} placeholder="Dr." />
      <F label="Vorname" span={2} value={data.first_name} onChange={(v) => set("first_name", v)} testid={`${prefix}-first-name`} />
      <F label="Nachname" span={2} value={data.last_name} onChange={(v) => set("last_name", v)} testid={`${prefix}-last-name`} />
      <F label="Position" span={2} value={data.position} onChange={(v) => set("position", v)} testid={`${prefix}-position`} />
      <F label="E-Mail" span={2} type="email" value={data.email} onChange={(v) => set("email", v)} testid={`${prefix}-email`} />
      <F label="Telefon" span={1} value={data.phone} onChange={(v) => set("phone", v)} testid={`${prefix}-phone`} />
      <F label="Mobil" span={1} value={data.mobile} onChange={(v) => set("mobile", v)} testid={`${prefix}-mobile`} />
    </>
  );
}

export function AdSection({ form, set, formats, type, bare }) {
  const pick = (id) => {
    const f = formats.find((x) => x.id === id);
    if (!f) { set({ ad_format_id: "", ad_format: "", ad_size: "" }); return; }
    const size = f.width_mm && f.height_mm ? `${f.width_mm}×${f.height_mm} mm${f.bleed_mm ? ` (+${f.bleed_mm} mm Beschnitt)` : ""}` : "";
    set({ ad_format_id: f.id, ad_format: f.name, ad_size: size, ...(type === "bezahlkunde" && !Number(form.amount) && f.price ? { amount: f.price } : {}) });
  };
  const body = (
    <>
      <div className="col-span-6 sm:col-span-3">
        <Label>Anzeigenformat</Label>
        <Select value={form.ad_format_id || "__none__"} onValueChange={(v) => pick(v === "__none__" ? "" : v)}>
          <SelectTrigger className="mt-1" data-testid="customer-ad-format-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">– kein Format –</SelectItem>
            {formats.map((f) => {
              const taken = type === "partner" && f.taken_by && f.taken_by.id !== form.id;
              return <SelectItem key={f.id} value={f.id} disabled={taken} data-testid={`ad-format-option-${f.id}`}>{f.name}{f.width_mm ? ` · ${f.width_mm}×${f.height_mm} mm` : ""}{taken ? ` – vergeben an ${f.taken_by.name}` : ""}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        {formats.length === 0 && <p className="text-xs text-amber-600 mt-1">Noch keine Formate – unter Einstellungen → Anzeigenformate anlegen.</p>}
      </div>
      <div className="col-span-6 sm:col-span-3">
        <Label>Größe</Label>
        <div className="mt-1 h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="customer-ad-size">{form.ad_size || "–"}</div>
      </div>
      {type === "bezahlkunde" && (
        <F label="Preis (€ netto, zzgl. MwSt.)" span={2} type="number" value={form.amount} onChange={(v) => set("amount", v)} testid="customer-amount" />
      )}
    </>
  );
  if (bare) return <div className="grid grid-cols-6 gap-3" data-testid="section-ad">{body}</div>;
  return <Section title={type === "partner" ? "Freianzeige" : "Zuletzt gebuchte Anzeige"} testid="section-ad">{body}</Section>;
}

export function NotesSection({ value, onChange }) {
  return (
    <Section title="Notizen" testid="section-notes">
      <div className="col-span-6"><Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} data-testid="customer-notes" /></div>
    </Section>
  );
}
